/**
 * Production adapter for `set_structure_visibility`: the structure list's
 * read model and the SAME event the eye icon publishes
 * (`RoiVisibilityChangeRequestedEvent`, as `useRoi().setVisible` and the
 * list's show/hide-all do). Browser glue covered by the manual demo; the
 * logic is unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import {
    domainEventBus,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import type { VisibilityDeps } from './visibility';

export function useStructureVisibilityDeps(): () => VisibilityDeps {
    const structureSet = useRecoilValue(currentStructureSetState);
    const selectedRoi = useRecoilValue(selectedROIState);

    return useCallback(
        (): VisibilityDeps => ({
            structures: (structureSet?.roiArray ?? []).map((r) => ({
                roiNumber: r.roiNumber,
                name: r.name,
                type: r.interpretedType,
            })),
            selectedRoiNumber: selectedRoi >= 0 ? selectedRoi : undefined,
            setVisible: (roiNumber, visible) => {
                domainEventBus
                    .publish(
                        new RoiVisibilityChangeRequestedEvent(
                            roiNumber,
                            visible,
                            'user'
                        )
                    )
                    .catch((error) => {
                        console.error(
                            'set_structure_visibility: event failed:',
                            error
                        );
                    });
            },
        }),
        [structureSet, selectedRoi]
    );
}
